import { Row, flexRender } from '@tanstack/react-table';
import { useDrag, useDrop } from 'react-dnd';
import { IconButton, Tooltip, useDisclosure } from '@chakra-ui/react';
import { IconPencil, IconTrash, IconGripVertical } from '@tabler/icons-react';
import { generatePath, Link } from 'react-router-dom';

import TableDndSpacer from 'components/Table/TableDndSpacer';
import ROUTES from 'app/routes';
import { DragItem } from 'types/common';
import CustomModal from 'components/CustomModal';
import Td from 'pages/OnboardScreens/Listing/components/Table/components/Td';

interface DraggableRowProps {
  row: Row<OnboardScreen>;
  handleReorder: (payload: { id: number; targetRowIndex: number }) => void;
  handleDelete: (id: number) => void;
  screenId: number;
}

function DraggableRow({
  row,
  handleReorder,
  handleDelete,
  screenId,
}: DraggableRowProps) {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [{ isOver, targetOffset }, dropRef] = useDrop({
    accept: 'row',
    collect(monitor) {
      return {
        isOver: monitor.isOver(),
        targetOffset: monitor.getDifferenceFromInitialOffset()?.y,
      };
    },
    drop: (item: DragItem) => {
      const sourcePlacement = item.placement;
      const targetPlacement = row.original.position;
      if (sourcePlacement === targetPlacement) return;
      handleReorder({ id: item.id, targetRowIndex: targetPlacement });
    },
  });

  const [{ isDragging }, dragRef, previewRef] = useDrag({
    type: 'row',
    item: () => {
      return { id: row.original.id, placement: row.original.position };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const showDrop = isOver && !isDragging && targetOffset;

  return (
    <>
      {showDrop && targetOffset < 0 && <TableDndSpacer />}
      <tr ref={previewRef} key={`body_row_${row.original.id}_${row.id}`}>
        {row.getVisibleCells().map((cell, idx, { length }) => {
          if (idx + 1 === length) return null;
          return (
            <Td key={`body_column_${row.original.id}_${cell.id}`}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </Td>
          );
        })}
        <Td ref={dropRef}>
          <>
            <CustomModal
              confirmationText="Usuń"
              onAccept={() => handleDelete(row.original.id)}
              cancellationText="Anuluj"
              onClose={onClose}
              isOpen={isOpen}
              title="Czy na pewno chcesz trwale usunąć ten ekran?"
            />
            <Tooltip label="Edytuj" openDelay={1000}>
              <IconButton
                width="72px"
                height="56px"
                icon={<IconPencil size={20} />}
                aria-label="visiblity"
                variant="ghost"
                as={Link}
                to={generatePath(ROUTES.onboardScreens.edit, {
                  screenId,
                  id: row.original.id,
                })}
              />
            </Tooltip>
            <Tooltip label="Usuń" openDelay={1000}>
              <IconButton
                width="72px"
                height="56px"
                icon={<IconTrash size={20} />}
                aria-label="visiblity"
                variant="ghost"
                onClick={onOpen}
              />
            </Tooltip>
            <IconButton
              width="72px"
              height="56px"
              icon={<IconGripVertical size={20} />}
              aria-label="visiblity"
              variant="ghost"
              ref={dragRef}
            />
          </>
        </Td>
      </tr>
      {showDrop && targetOffset > 0 && <TableDndSpacer />}
    </>
  );
}

export default DraggableRow;
